import type {
  DailyGoalHistoryDay,
  TodayDashboardResponse,
} from "@consistency-circle/shared";
import { addDays } from "../../lib/goal-stats";
import type { SlotItem, Timeline } from "./types";

export type { SlotItem, Timeline } from "./types";

export function localDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function greetingForHour(hour: number): string {
  if (hour < 5) return "Up late";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/** Key the dismissal to the upcoming Monday so it spans the whole weekend. */
export function planWeekKey(localDate: string): string {
  const dow = new Date(`${localDate}T12:00:00Z`).getUTCDay();
  const daysToMonday = dow === 6 ? 2 : dow === 0 ? 1 : 8 - dow;
  return addDays(localDate, daysToMonday);
}

/** Minutes before due time at which the hero counts as "Now". */
export const NOW_LEAD_MINUTES = 30;

export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function formatDueTime(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date();
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

export function minutesUntil(hhmm: string, now: Date): number {
  return toMinutes(hhmm) - (now.getHours() * 60 + now.getMinutes());
}

export function formatRelative(minutes: number): string {
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const span = h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
  return minutes >= 0 ? `in ${span}` : `${span} ago`;
}

function byDueTime(a: SlotItem, b: SlotItem): number {
  return (
    toMinutes(a.slot.dueTimeLocal) - toMinutes(b.slot.dueTimeLocal) ||
    a.slot.sortOrder - b.slot.sortOrder ||
    a.slot.name.localeCompare(b.slot.name)
  );
}

export function deriveTimeline(data: TodayDashboardResponse): Timeline {
  const items: SlotItem[] = data.goals.flatMap((goal) =>
    goal.slots.map((slot) => ({
      slot,
      goalId: goal.id,
      goalName: goal.name,
      notifyCircleName: goal.notifyCircleName ?? null,
    })),
  );

  const recover = items.filter((i) => i.slot.status === "MISSED").sort(byDueTime);
  const pending = items.filter((i) => i.slot.status === "DUE_LATER").sort(byDueTime);

  const doneItems = items
    .filter((i) => i.slot.status === "DONE")
    .sort((a, b) => (b.slot.completedAt ?? "").localeCompare(a.slot.completedAt ?? ""));
  const skippedItems = items.filter((i) => i.slot.status === "SKIPPED").sort(byDueTime);

  return {
    hero: pending[0] ?? null,
    recover,
    upNext: pending.slice(1),
    done: [...doneItems, ...skippedItems],
  };
}

export function isWindowOpen(item: SlotItem, now: Date): boolean {
  return minutesUntil(item.slot.dueTimeLocal, now) <= NOW_LEAD_MINUTES;
}

/** Mean completion percent across the given dates (today included via percentByDate). */
export function weeklyConsistency(
  dates: string[],
  percentByDate: Map<string, number>,
): number {
  if (dates.length === 0) return 0;
  const sum = dates.reduce((acc, d) => acc + (percentByDate.get(d) ?? 0), 0);
  return Math.round(sum / dates.length);
}

/**
 * Streak of days with at least one completion, where rest days are protected:
 * a day with nothing scheduled, or where everything was deliberately skipped,
 * neither extends nor breaks the run. Today never breaks the streak — the day
 * is not over — it only extends it once something is done.
 */
export function computeStreakWithToday(
  history: DailyGoalHistoryDay[],
  todayDate: string,
  todayCompletedCount: number,
): number {
  const byDate = new Map(history.map((d) => [d.localDate, d]));

  let streak = todayCompletedCount > 0 ? 1 : 0;
  let current = todayDate;
  for (;;) {
    const prev = addDays(current, -1);
    const day = byDate.get(prev);
    if (!day) break;
    if (day.completedCount > 0) {
      streak++;
    } else if (day.totalCount > 0) {
      // Scheduled actions left undone and not skipped: the run ends here.
      break;
    }
    current = prev;
  }
  return streak;
}
